import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../css/Header.css";


function Header({ lang, kareLang, onLanguageChange, content }) {
    // 요청하신 대로, 기존의 모든 상태와 로직을 그대로 유지합니다.
    const [navActive, setNavActive] = useState(false);
    const navRef = useRef(null);
    const [mSearchActive, setMSearchActive] = useState(false);
    const msearchWrap = useRef(null);
    const burgerCheck = useRef();
    const [searchQuery, setSearchQuery] = useState("");
    const [mobileSearchQuery, setMobileSearchQuery] = useState("");
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
    const [mobileLanguageMenuOpen, setMobileLanguageMenuOpen] = useState(false);
    const languageMenuRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // 페이지 이동 시 모든 모바일 메뉴 상태 초기화
        setNavActive(false);
        if (navRef.current && navRef.current.classList.contains("active")) {
            navRef.current.classList.remove("active");
        }
        if (burgerCheck.current) {
            burgerCheck.current.checked = false;
        }
        setMSearchActive(false);
        setMobileSearchQuery("");
        setSearchQuery("");
        setLanguageMenuOpen(false);
        setMobileLanguageMenuOpen(false);
    }, [location]);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (languageMenuRef.current && !languageMenuRef.current.contains(e.target)) {
                setLanguageMenuOpen(false);
            }
        };
        if (languageMenuOpen) {
            document.addEventListener("click", handleOutsideClick);
        }
        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [languageMenuOpen]);

    const handleSearch = (e) => {
        e.preventDefault(); // form 태그의 새로고침을 막기 위해 추가
        if (searchQuery) {
            navigate(`/product?search=${searchQuery}`);
        }
    };

    const handleContactClick = () => {
        navigate("/?connect=true");
    };

    const handleMobileSearch = () => {
        if (mobileSearchQuery) {
            navigate(`/product?search=${mobileSearchQuery}`);
            setMobileSearchQuery("");
            setMSearchActive(false);
        }
    };

    const toggleMobileSearch = (e) => {
        e.stopPropagation();
        setMSearchActive(!mSearchActive);
    };

    const handleBackgroundClick = useCallback((e) => {
        const mSearchBtn = document.querySelector(".mSearchBtn");
        const isClickInsideSearch = msearchWrap.current?.contains(e.target);
        const isClickOnSearchBtn = mSearchBtn?.contains(e.target);
        if (!isClickInsideSearch && !isClickOnSearchBtn) {
            setMSearchActive(false);
        }
    }, []);

    useEffect(() => {
        if (mSearchActive) {
            const timer = setTimeout(() => {
                document.addEventListener("click", handleBackgroundClick);
            }, 100);
            return () => {
                clearTimeout(timer);
                document.removeEventListener("click", handleBackgroundClick);
            };
        }
    }, [mSearchActive, handleBackgroundClick]);

    useEffect(() => {
        if (navRef.current) {
            if (navActive) {
                navRef.current.classList.add("active");
            } else {
                navRef.current.classList.remove("active");
            }
        }
        // navActive 상태와 체크박스 상태를 동기화
        if (burgerCheck.current) {
            burgerCheck.current.checked = navActive;
        }
    }, [navActive]);

    useEffect(() => {
        if (mSearchActive) {
            msearchWrap.current.classList.add("active");
        } else {
            msearchWrap.current.classList.remove("active");
        }
    }, [mSearchActive]);

    const handleMobileSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            handleMobileSearch();
        }
    };

    const handleLanguageSelect = (selectedLang) => {
        onLanguageChange(selectedLang);
        setLanguageMenuOpen(false);
        setMobileLanguageMenuOpen(false);
    };

    return (
    <header>
        {/* 1. 로고 (변경 없음) */}
        <h1>
            <Link to={"/"}>
                <img src="/img/header_logo_img.png" alt="logo" />
                <span className="desktop-logo-text">Spatial Core Inc.</span>
            </Link>
        </h1>

        {/* --- 수정된 부분 --- */}
        {/* 메뉴와 검색창을 하나의 그룹으로 묶습니다. */}
        <div className="header-right-section">
            {/* 2. 네비게이션 메뉴 (데스크탑용) */}
            {location.pathname !== '/map' && (
                <div className="language-menu" ref={languageMenuRef}>
                    <button
                        onClick={() => setLanguageMenuOpen((prev) => !prev)}
                        className="lang-toggle-btn"
                        type="button"
                    >
                        Language
                    </button>
                    {languageMenuOpen && (
                        <ul className="language-dropdown">
                            <li>
                                <button
                                    type="button"
                                    className={lang === "ko" && kareLang !== "ar" ? "active" : ""}
                                    onClick={() => handleLanguageSelect("ko")}
                                >
                                    한국어 (Korean)
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className={lang === "en" && kareLang !== "ar" ? "active" : ""}
                                    onClick={() => handleLanguageSelect("en")}
                                >
                                    영어 (English)
                                </button>
                            </li>
                            <li>
                                <button
                                    type="button"
                                    className={kareLang === "ar" ? "active" : ""}
                                    onClick={() => handleLanguageSelect("ar")}
                                >
                                    아랍어 (العربية)
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            )}
            {/* 3. 데스크탑 네비게이션 메뉴 */}
            <nav className="desktop-nav">
                <ul className="navWrap">
                    <li><a href="/business-travel/index.html">{content.headerNavBusinessTravel}</a></li>
                    <li><a href="https://tour.spatialcore.ai/">TOUR</a></li>
                    <li><Link to="/kare">{content.headerNavKare}</Link></li>
                    <li><Link to="/map">{content.headerNav1}</Link></li>
                    <li><Link to="/developer">{content.headerNav2}</Link></li>
                    <li>
                        <button type="button" className="nav-link-btn" onClick={handleContactClick}>
                            {content.headerNav3}
                        </button>
                    </li>
                </ul>
            </nav>

            {/* 언어 변경 버튼을 추가 (Map 페이지에서는 숨김) */}

            {/* 3. 검색창 (데스크탑용) */}
            {/* <div className="desktop-searchWrap">
                <form onSubmit={handleSearch}>
                    <input
                        type="text"
                        placeholder="키워드로 데이터를 검색해보세요."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <button type="submit" className="searchBtn-wrapper">
                        <img
                            className="searchBtn-icon"
                            src={process.env.PUBLIC_URL + "/img/icon_search_white.png"}
                            alt="search"
                        />
                    </button>
                </form>
            </div> */}
        </div>

            {/* 4. 기존 모바일 UI (로직 유지를 위해 JSX 구조는 그대로 두고, CSS로 제어) */}
            <div className="mobile-ui-container">
                <nav ref={navRef}>
                    <ul className="navWrap">
                        {/* 기존 모바일 메뉴 구조 */}
                        {/* <li><p><Link to="/product">데이터 상품</Link></p></li> */}
                        {location.pathname !== '/map' && (
                            <li className="mobile-language-menu">
                                <button
                                    type="button"
                                    className="mobile-language-trigger"
                                    onClick={() => setMobileLanguageMenuOpen((prev) => !prev)}
                                >
                                    Language
                                </button>
                                {mobileLanguageMenuOpen && (
                                    <ul className="mobile-language-dropdown">
                                        <li>
                                            <button
                                                type="button"
                                                className={lang === "ko" && kareLang !== "ar" ? "active" : ""}
                                                onClick={() => handleLanguageSelect("ko")}
                                            >
                                                한국어 (Korean)
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={lang === "en" && kareLang !== "ar" ? "active" : ""}
                                                onClick={() => handleLanguageSelect("en")}
                                            >
                                                영어 (English)
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={kareLang === "ar" ? "active" : ""}
                                                onClick={() => handleLanguageSelect("ar")}
                                            >
                                                العربية
                                            </button>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        )}
                        {/* 모바일 네비게이션 메뉴 */}
                        <li><p><a href="/business-travel/index.html">{content.headerNavBusinessTravel}</a></p></li>
                        <li><p><a href="https://tour.spatialcore.ai/">TOUR</a></p></li>
                        <li><p><Link to="/kare">{content.headerNavKare}</Link></p></li>
                        <li><p><Link to="/map">{content.headerNav1}</Link></p></li>
                        <li><p><Link to="/developer">{content.headerNav2}</Link></p></li>
                        <li>
                            <p>
                                <button type="button" className="mobile-nav-link-btn" onClick={handleContactClick}>
                                    {content.headerNav3}
                                </button>
                            </p>
                        </li>
                    </ul>
                </nav>
                <div className="rightWrap">
                    <div className="searchWrap">
                        {/* 이 검색창은 이제 데스크탑용으로 대체됨 */}
                    </div>
                    {!mSearchActive && (
                        <div className="mSearchBtn">
                            <img
                                className="searchBtn"
                                onClick={toggleMobileSearch}
                                src={process.env.PUBLIC_URL + "/img/icon_search_white.png"}
                                alt="search"
                            />
                        </div>
                    )}
                    <div className="nav">
                        <input
                            className="burger-check"
                            type="checkbox"
                            id="burger-check"
                            ref={burgerCheck}
                            onChange={(e) => setNavActive(e.target.checked)}
                        />
                        <label className="burger-icon" htmlFor="burger-check">
                            <span className="burger-sticks"></span>
                        </label>
                    </div>
                </div>
                <div className="mSearchWrap" ref={msearchWrap} onClick={(e) => e.stopPropagation()}>
                    <input
                        type="text"
                        placeholder="키워드로 데이터를 검색해보세요."
                        value={mobileSearchQuery}
                        onChange={(e) => setMobileSearchQuery(e.target.value)}
                        onKeyPress={handleMobileSearchKeyPress}
                    />
                    <span>
                        <img
                            src={process.env.PUBLIC_URL + "/img/icon_search_white.png"}
                            onClick={handleMobileSearch}
                            alt="mobile search"
                        />
                    </span>
                </div>
            </div>
        </header>
    );
}

export default Header;